import { ECOLE_DEFAULT_VIDEO, useBackgroundVideo } from "@/hooks/useBackgroundVideo";
import { useFlashcards } from "@/hooks/useFlashcards";
import { useRouter } from "@/hooks/useSchoolRouter";
import { normalizePaywallSource } from "@/lib/analytics/paywall-source";
import { trackUpgradeIntent } from "@/lib/analytics/posthog-helpers";
import type { ConversationStartChoice } from "@/lib/conversationContinuation";
import { buildTrackedPaywallUrl } from "@/lib/paywallUrl";
import {
  PERSONALIZATION_CUSTOM_INSTRUCTIONS_MAX_CHARS,
  PERSONALIZATION_NICKNAME_MAX_CHARS,
  capCustomInstructionsInput,
  capNicknameInput,
} from "@/lib/promptPersonalization";
import {
  COMMON_WORD_LIMITS,
  SESSION_VOCABULARY_SELECTION_EVENT,
  type SessionVocabularyContext,
  buildCommonWordsVocabularyContext,
  buildFlashcardsSessionVocabularyContext,
  loadSessionVocabularySelection,
  saveSessionVocabularySelection,
} from "@/lib/sessionVocabulary";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { usePostHog } from "posthog-js/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import languages from "../data/languages.json";
import {
  FREE_VOICES,
  PREMIUM_VOICES,
  VOICE_DESCRIPTIONS,
  VOICE_DISPLAY_NAMES,
  VoiceKey,
} from "../data/voiceDisplayNames";
import { Button } from "../ui/button";
import { useAuth } from "./AuthContext";
import {
  ProFeatureUpgradeModal,
  type ProFeatureUpgradeModalFeature,
} from "./ProFeatureUpgradeModal";
import { TutorWhiteboardToggle } from "./fullscreen/TutorWhiteboardToggle";

type TeachingStyle = "neutral" | "cheeky" | "chic" | "spartan";

const TUTOR_AVATAR_IMAGE_SRC: Record<VoiceKey, string> = {
  Puck: "/ui/Tutors/Joshua.png",
  Charon: "/ui/Tutors/Ethan.png",
  Kore: "/ui/Tutors/Sam.jpg",
  Fenrir: "/ui/Tutors/Bryan.png",
  Aoede: "/ui/Tutors/Phoebe.png",
  Leda: "/ui/Tutors/Joanna.png",
  Orus: "/ui/Tutors/Markus.png",
  Zephyr: "/ui/Tutors/Lisa.png",
};

function TutorAvatarPreview({
  voice,
  className = "h-9 w-9",
}: {
  voice: VoiceKey;
  className?: string;
}) {
  const imageSrc = TUTOR_AVATAR_IMAGE_SRC[voice];

  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-[#181b28] ${className}`}
    >
      {imageSrc ? (
        <Image
          src={imageSrc}
          alt=""
          aria-hidden="true"
          fill
          sizes="36px"
          className="object-cover"
          loading="lazy"
        />
      ) : (
        <span className="text-sm font-semibold text-white/75" aria-hidden="true">
          {VOICE_DISPLAY_NAMES[voice]?.charAt(0) || "?"}
        </span>
      )}
    </span>
  );
}

interface BackgroundVideoSelectorProps {
  defaultSelectedId?: string;
  fallbackVideoUrl?: string;
}

// Background Video Selector Component with mini previews
function BackgroundVideoSelector({
  defaultSelectedId,
  fallbackVideoUrl,
}: BackgroundVideoSelectorProps) {
  const { state, selectPreset, setCustomUrl, presets } = useBackgroundVideo({
    defaultState: defaultSelectedId
      ? { selectedId: defaultSelectedId, customUrl: null }
      : undefined,
    fallbackVideoUrl,
  });
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customUrlInput, setCustomUrlInput] = useState(state.customUrl || "");
  const [urlError, setUrlError] = useState<string | null>(null);

  const validateUrl = (url: string): boolean => {
    if (!url.trim()) {
      setUrlError("Please enter a URL");
      return false;
    }
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov"];
    const isDirectLink = videoExtensions.some((ext) => url.toLowerCase().includes(ext));
    if (!isDirectLink) {
      setUrlError("Please use a direct video URL (e.g., .mp4, .webm)");
      return false;
    }
    if (url.includes("youtube.com") || url.includes("youtu.be") || url.includes("vimeo.com")) {
      setUrlError("YouTube and Vimeo links are not supported. Use a direct video URL.");
      return false;
    }
    setUrlError(null);
    return true;
  };

  const handleSaveCustomUrl = () => {
    if (validateUrl(customUrlInput)) {
      setCustomUrl(customUrlInput);
      setShowCustomInput(false);
    }
  };

  return (
    <div>
      <div className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-200">
        Background video
      </div>
      {/* Video preview grid */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {presets.map((preset) => (
          <button
            key={preset.id}
            onClick={() => selectPreset(preset.id)}
            className={`relative aspect-video rounded-lg overflow-hidden transition-all ${
              state.selectedId === preset.id
                ? "ring-2 ring-orange-500 ring-offset-2 ring-offset-[#1c202e]"
                : "ring-1 ring-gray-700/50 hover:ring-orange-500/50"
            }`}
            title={preset.label}
          >
            {/* Mini video preview or black background */}
            {preset.url ? (
              <video
                src={preset.url}
                className="w-full h-full object-cover"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
              />
            ) : (
              <div className="w-full h-full bg-black" />
            )}
            {/* Overlay with label */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end justify-center pb-1">
              <span className="text-[10px] font-medium text-white/90 truncate px-1">
                {preset.label}
              </span>
            </div>
            {/* Selected background */}
            {state.selectedId === preset.id && (
              <div className="absolute right-1 top-1 rounded-md bg-orange-500 p-0.5">
                <span className="text-[9px] font-semibold text-white">Selected</span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Custom URL option */}
      <button
        onClick={() => setShowCustomInput(!showCustomInput)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-all ${
          state.selectedId === "custom"
            ? "border border-orange-400/60 bg-orange-500/15 text-orange-200"
            : "border border-white/10 bg-white/[0.04] text-white hover:border-orange-300/35 hover:bg-white/[0.07]"
        }`}
      >
        <span className="inline-flex items-center gap-2">Custom URL</span>
        {state.selectedId === "custom" && <span className="text-xs text-orange-400">Selected</span>}
      </button>

      {/* Custom URL input */}
      {showCustomInput && (
        <div className="mt-2 space-y-2 p-3 bg-[#1c1f2e] rounded-lg border border-gray-700/50">
          <input
            type="url"
            value={customUrlInput}
            onChange={(e) => {
              setCustomUrlInput(e.target.value);
              setUrlError(null);
            }}
            placeholder="https://cdn.example.com/video.mp4"
            className="w-full px-3 py-2 bg-[#262b3d] border border-gray-700 rounded-md text-white text-sm placeholder-gray-500 focus:outline-none focus:border-orange-500"
          />
          {urlError && <p className="text-red-400 text-xs">{urlError}</p>}
          <p className="text-gray-400 text-xs">
            Use a direct link to a video file. YouTube/Vimeo links are not supported.
          </p>
          <Button
            onClick={handleSaveCustomUrl}
            className="w-full rounded-md border border-orange-300/70 bg-orange-500 py-2 text-sm font-semibold text-white transition-colors hover:bg-orange-400"
          >
            Save Custom Video
          </Button>
        </div>
      )}
    </div>
  );
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedVoice: VoiceKey;
  setSelectedVoice: (voice: VoiceKey) => void;
  selectedTeachingStyle: TeachingStyle;
  setSelectedTeachingStyle: (style: TeachingStyle) => void;
  nickname: string;
  setNickname: (nickname: string) => void;
  customInstructions: string;
  setCustomInstructions: (instructions: string) => void;
  selectedNoAvatar: boolean;
  setSelectedNoAvatar: (noAvatar: boolean) => void;
  selectedLanguageCode?: string;
  selectedLanguageName?: string;
  isAvatarForcedOff?: boolean;
  subscribed?: boolean;
  backgroundVideoDefaultId?: string;
  backgroundVideoFallbackUrl?: string;
  allowBackgroundSelection?: boolean;
  conversationStartChoice: ConversationStartChoice;
  setConversationStartChoice: (choice: ConversationStartChoice) => void;
  examplesBeforePractice?: boolean;
  onExamplesBeforePracticeChange?: (enabled: boolean) => void;
  tutorWhiteboardEnabled?: boolean;
  onTutorWhiteboardEnabledChange?: (enabled: boolean) => void;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  selectedVoice,
  setSelectedVoice,
  selectedTeachingStyle,
  setSelectedTeachingStyle,
  nickname,
  setNickname,
  customInstructions,
  setCustomInstructions,
  selectedNoAvatar,
  setSelectedNoAvatar,
  selectedLanguageCode,
  selectedLanguageName,
  isAvatarForcedOff = false,
  subscribed = false,
  backgroundVideoDefaultId,
  backgroundVideoFallbackUrl,
  allowBackgroundSelection = true,
  conversationStartChoice,
  setConversationStartChoice,
  examplesBeforePractice = true,
  onExamplesBeforePracticeChange,
  tutorWhiteboardEnabled = false,
  onTutorWhiteboardEnabledChange,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { flashcards } = useFlashcards(isOpen ? user?.id : undefined);
  const posthog = usePostHog();
  const router = useRouter();
  const [upgradeFeature, setUpgradeFeature] = useState<ProFeatureUpgradeModalFeature | null>(null);
  const languageCodeByName = languages as Record<string, string>;
  const vocabularyLanguage = useMemo(() => {
    if (selectedLanguageName) return selectedLanguageName;
    if (!selectedLanguageCode) return "";
    return (
      Object.entries(languageCodeByName).find(([, code]) => code === selectedLanguageCode)?.[0] ||
      selectedLanguageCode
    );
  }, [languageCodeByName, selectedLanguageCode, selectedLanguageName]);
  const currentLanguageCode =
    selectedLanguageCode || (vocabularyLanguage ? languageCodeByName[vocabularyLanguage] : "");
  const hasVocabularyLanguage = Boolean(vocabularyLanguage && currentLanguageCode);
  const [vocabularyStatus, setVocabularyStatus] = useState<string | null>(null);
  const [vocabularySelection, setVocabularySelection] = useState<SessionVocabularyContext | null>(
    null
  );
  const showTutorAvatar = !isAvatarForcedOff && !selectedNoAvatar;
  const avatarToggleLabel = isAvatarForcedOff
    ? "Avatar available after free signup"
    : showTutorAvatar
      ? "Hide avatar"
      : "Show avatar";

  const handleUpgrade = () => {
    const paywallSource = normalizePaywallSource("settings_modal");
    trackUpgradeIntent(posthog, "settings_modal", {
      user_id: user?.id,
      email: user?.email,
      subscribed,
      paywall_source: paywallSource,
    });

    router.push(buildTrackedPaywallUrl({ source: paywallSource }));
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && e.target === e.currentTarget) {
      onClose();
    }
  };

  const vocabularyLanguageFlashcards = useMemo(
    () =>
      hasVocabularyLanguage
        ? flashcards.filter((flashcard) => flashcard.target_language === vocabularyLanguage)
        : [],
    [flashcards, hasVocabularyLanguage, vocabularyLanguage]
  );
  const vocabularyLanguageFavorites = useMemo(
    () => vocabularyLanguageFlashcards.filter((flashcard) => flashcard.is_favorite),
    [vocabularyLanguageFlashcards]
  );

  const handleUseCommonWords = useCallback(
    (wordLimit: (typeof COMMON_WORD_LIMITS)[number]) => {
      if (!hasVocabularyLanguage) {
        setVocabularyStatus("Choose a practice language first.");
        return;
      }
      saveSessionVocabularySelection(
        buildCommonWordsVocabularyContext({
          languageCode: currentLanguageCode,
          languageName: vocabularyLanguage,
          wordLimit,
        })
      );
      setVocabularyStatus(`${wordLimit} most common ${vocabularyLanguage} words selected.`);
    },
    [currentLanguageCode, hasVocabularyLanguage, vocabularyLanguage]
  );

  const handleUseFlashcardsForSession = useCallback(
    (mode: "all" | "favorites") => {
      if (!hasVocabularyLanguage) {
        setVocabularyStatus("Choose a practice language first.");
        return;
      }
      const sourceCards =
        mode === "favorites" ? vocabularyLanguageFavorites : vocabularyLanguageFlashcards;
      const context = buildFlashcardsSessionVocabularyContext(sourceCards, {
        deckId: `flashcards:${vocabularyLanguage}${mode === "favorites" ? ":favorites" : ""}`,
        deckName: `${vocabularyLanguage} ${mode === "favorites" ? "favorite " : ""}flashcards`,
        languageCode: currentLanguageCode,
        sourceLabel: `${vocabularyLanguage} ${mode === "favorites" ? "favorite " : ""}flashcards`,
      });
      if (!context) {
        setVocabularyStatus(
          `No ${mode === "favorites" ? "favorite " : ""}${vocabularyLanguage} flashcards available.`
        );
        return;
      }
      saveSessionVocabularySelection(context);
      setVocabularyStatus(
        `${context.cards?.length || 0} ${vocabularyLanguage} flashcard${
          context.cards?.length === 1 ? "" : "s"
        } selected.`
      );
    },
    [
      currentLanguageCode,
      hasVocabularyLanguage,
      vocabularyLanguage,
      vocabularyLanguageFavorites,
      vocabularyLanguageFlashcards,
    ]
  );

  const handleClearSessionVocabulary = useCallback(() => {
    saveSessionVocabularySelection(null);
    setVocabularyStatus(null);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const sync = () => setVocabularySelection(loadSessionVocabularySelection());
    sync();
    window.addEventListener(SESSION_VOCABULARY_SELECTION_EVENT, sync);
    return () => window.removeEventListener(SESSION_VOCABULARY_SELECTION_EVENT, sync);
  }, [isOpen]);

  const selectionMatchesLanguage =
    hasVocabularyLanguage &&
    (vocabularySelection?.languageName === vocabularyLanguage ||
      vocabularySelection?.languageCode === currentLanguageCode);
  const activeCommonWordLimit =
    vocabularySelection?.mode === "common_words" && selectionMatchesLanguage
      ? vocabularySelection.wordLimit
      : null;
  const savedCardsDeckId = `flashcards:${vocabularyLanguage}`;
  const favoritesDeckId = `flashcards:${vocabularyLanguage}:favorites`;
  const isSavedCardsActive =
    vocabularySelection?.mode === "flashcards" && vocabularySelection.deckId === savedCardsDeckId;
  const isFavoritesActive =
    vocabularySelection?.mode === "flashcards" && vocabularySelection.deckId === favoritesDeckId;
  const hasNoVocabularyLimit = !vocabularySelection || vocabularySelection.mode === "none";
  const currentVocabularyLabel = hasNoVocabularyLimit
    ? "No limit"
    : vocabularySelection?.sourceLabel || "Custom selection";

  // Ensure non-subscribed users cannot have a premium voice selected
  useEffect(() => {
    if (isOpen && !subscribed && !FREE_VOICES.includes(selectedVoice)) {
      const fallback = FREE_VOICES[0];
      setSelectedVoice(fallback);
    }
  }, [isOpen, subscribed, selectedVoice, setSelectedVoice]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/75 p-4"
        onClick={handleBackdropClick}
      >
        <motion.div
          ref={modalRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby="settings-dialog-title"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative max-h-[90vh] w-full max-w-md overflow-y-auto overscroll-contain rounded-lg border border-white/10 bg-[#121318] p-5 shadow-[0_28px_90px_rgba(0,0,0,0.55)] sm:p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute right-3 top-3 flex h-11 items-center justify-center rounded-md border border-white/10 px-3 text-sm text-white/55 transition-colors hover:bg-white/[0.06] hover:text-white"
            aria-label="Close settings modal"
          >
            Close
          </button>
          <motion.h2
            id="settings-dialog-title"
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            className="mb-6 flex items-center gap-3 text-2xl font-bold text-white"
          >
            Settings
          </motion.h2>
          <div className="space-y-6">
            {!subscribed && user && (
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="flex items-center justify-between gap-3 rounded-lg border border-orange-400/20 bg-orange-500/[0.08] p-4"
              >
                <div className="text-sm font-medium text-white/70">
                  <span className="font-bold">Unlock premium voices</span> and unlimited sessions
                  with <span className="font-extrabold text-orange-300">PRO</span>.
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleUpgrade()}
                  className="flex h-10 shrink-0 items-center gap-2 rounded-lg border border-orange-300/70 bg-orange-500 px-3 text-sm font-bold text-white transition-colors hover:bg-orange-400"
                >
                  Upgrade
                </motion.button>
              </motion.div>
            )}
            <fieldset className="rounded-lg border border-white/10 bg-white/[0.025] p-4">
              <legend className="px-1 text-sm font-bold text-gray-200">When speaking starts</legend>
              <div className="mt-2 grid gap-2">
                {(
                  [
                    ["new", "New Session", "Chicky still remembers your progress."],
                    ["continue_last", "Resume last session", null],
                  ] as const
                ).map(([value, label, description]) => {
                  const isProLocked = value === "continue_last" && !subscribed;
                  const isSelected = (subscribed ? conversationStartChoice : "new") === value;

                  return (
                    <label
                      key={value}
                      aria-disabled={isProLocked}
                      className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm font-semibold ${
                        isProLocked
                          ? "cursor-pointer border-white/[0.06] bg-white/[0.02] text-gray-400 transition-colors hover:border-orange-300/25 hover:bg-orange-500/[0.06]"
                          : "cursor-pointer border-white/10 bg-white/[0.035] text-white"
                      }`}
                    >
                      <input
                        type="radio"
                        name="conversation-start-choice"
                        value={value}
                        aria-label={label}
                        checked={isSelected}
                        aria-disabled={isProLocked}
                        onChange={() => {
                          if (isProLocked) {
                            setUpgradeFeature("resume_session");
                          } else {
                            setConversationStartChoice(value);
                          }
                        }}
                        className="h-4 w-4 cursor-pointer accent-orange-500"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block">{label}</span>
                        {description ? (
                          <span className="mt-0.5 block text-xs font-medium text-gray-400">
                            {description}
                          </span>
                        ) : null}
                      </span>
                      {isProLocked ? (
                        <span className="inline-flex items-center gap-1 rounded-md border border-orange-400/30 bg-orange-500/10 px-1.5 py-0.5 text-[10px] font-extrabold text-orange-200">
                          PRO
                        </span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
            </fieldset>
            {onTutorWhiteboardEnabledChange ? (
              <div>
                <div className="mb-2 text-sm font-bold text-gray-200">Visual exercises</div>
                <TutorWhiteboardToggle
                  checked={tutorWhiteboardEnabled}
                  onCheckedChange={onTutorWhiteboardEnabledChange}
                />
              </div>
            ) : null}
            <div>
              <div className="mb-2 flex items-center gap-2 text-sm font-bold text-gray-200">
                Voice
              </div>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  {(() => {
                    return (
                      <button className="flex w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:border-orange-300/35 hover:bg-white/[0.07]">
                        <span className="inline-flex items-center gap-3">
                          <TutorAvatarPreview voice={selectedVoice} className="h-8 w-8" />
                          {VOICE_DISPLAY_NAMES[selectedVoice]}
                        </span>
                      </button>
                    );
                  })()}
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                  sideOffset={6}
                  align="center"
                  className="z-[10001] mt-2 max-h-80 min-w-[220px] overflow-y-auto rounded-lg border border-white/10 bg-[#121318] py-1 shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
                >
                  <div className="px-3 py-1 text-[11px] uppercase text-gray-400">Free voices</div>
                  {FREE_VOICES.map((key) => {
                    return (
                      <DropdownMenu.Item
                        key={key}
                        onSelect={() => setSelectedVoice(key)}
                        className={`px-3 py-2 text-sm text-white hover:bg-orange-500/20 cursor-pointer ${
                          selectedVoice === key ? "bg-orange-500/10 text-orange-300" : ""
                        }`}
                      >
                        <span className="inline-flex items-center gap-3">
                          <TutorAvatarPreview voice={key} />
                          <span className="flex flex-col text-left">
                            <span>{VOICE_DISPLAY_NAMES[key]}</span>
                            <span className="text-[11px] text-gray-400 leading-tight">
                              {VOICE_DESCRIPTIONS[key]}
                            </span>
                          </span>
                        </span>
                      </DropdownMenu.Item>
                    );
                  })}
                  <DropdownMenu.Separator className="my-1 h-px bg-white/10" />
                  <div className="flex items-center gap-1 px-3 py-1 text-[11px] uppercase text-gray-400">
                    Premium voices{" "}
                    <span className="inline-flex items-center gap-1 rounded-md bg-orange-400 px-1.5 py-0.5 text-[10px] font-extrabold text-black">
                      PRO
                    </span>
                  </div>
                  {PREMIUM_VOICES.map((key) => {
                    const isProLocked = !subscribed;
                    return (
                      <DropdownMenu.Item
                        key={key}
                        aria-disabled={isProLocked}
                        onSelect={() => {
                          if (isProLocked) {
                            setUpgradeFeature("premium_voice");
                          } else {
                            setSelectedVoice(key);
                          }
                        }}
                        className={`px-3 py-2 text-sm cursor-pointer ${
                          isProLocked
                            ? "text-gray-500 hover:bg-orange-500/10 hover:text-white focus:bg-orange-500/10 focus:text-white"
                            : "text-white hover:bg-orange-500/20"
                        } ${selectedVoice === key ? "bg-orange-500/10 text-orange-300" : ""}`}
                      >
                        <span className="inline-flex items-center gap-3">
                          <TutorAvatarPreview voice={key} />
                          <span className="flex flex-col text-left">
                            <span>{VOICE_DISPLAY_NAMES[key]}</span>
                            <span className="text-[11px] text-gray-400 leading-tight">
                              {VOICE_DESCRIPTIONS[key]}
                            </span>
                          </span>
                          {!subscribed && (
                            <span className="inline-flex items-center gap-1 rounded-md border border-orange-400/30 bg-orange-500/10 px-1.5 py-0.5 text-[10px] text-orange-200">
                              Premium
                            </span>
                          )}
                        </span>
                      </DropdownMenu.Item>
                    );
                  })}
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.025] p-4">
              <label className="flex items-center justify-between gap-3">
                <div>
                  <span className="block text-sm font-bold text-gray-200">Show Avatar</span>
                  <span className="block text-xs text-gray-400 mt-1">
                    Display avatar during speaking sessions.
                  </span>
                </div>
                <button
                  type="button"
                  disabled={isAvatarForcedOff}
                  onClick={() => {
                    if (!isAvatarForcedOff) {
                      setSelectedNoAvatar(!selectedNoAvatar);
                    }
                  }}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    showTutorAvatar ? "bg-orange-500" : "bg-gray-600"
                  } ${isAvatarForcedOff ? "cursor-not-allowed opacity-70" : ""}`}
                  aria-label={avatarToggleLabel}
                  title={
                    isAvatarForcedOff
                      ? "Avatar available after free signup"
                      : showTutorAvatar
                        ? "Show avatar: on"
                        : "Show avatar: off"
                  }
                  aria-describedby={isAvatarForcedOff ? "guest-avatar-signup-note" : undefined}
                >
                  <div
                    className={`absolute w-4 h-4 bg-white rounded-full transition-transform top-1 ${
                      showTutorAvatar ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </label>
              {isAvatarForcedOff && (
                <p id="guest-avatar-signup-note" className="sr-only">
                  Avatar is available after free signup.
                </p>
              )}
            </div>
            <div className="space-y-4 rounded-lg border border-white/10 bg-white/[0.025] p-4">
              <div>
                <h3 className="text-sm font-bold text-gray-200">Personalisation</h3>
                <p className="mt-1 text-xs text-gray-400">
                  Saved on this device and applied to new speaking sessions.
                </p>
              </div>
              {onExamplesBeforePracticeChange ? (
                <button
                  type="button"
                  role="switch"
                  aria-label="Examples before practice"
                  aria-checked={examplesBeforePractice}
                  onClick={() => onExamplesBeforePracticeChange(!examplesBeforePractice)}
                  className="flex min-h-11 w-full items-center justify-between gap-4 rounded-lg border border-white/10 bg-black/25 px-3 py-2.5 text-left transition-colors hover:border-white/20 hover:bg-white/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/60"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-white">
                      Examples before practice
                    </span>
                    <span className="mt-0.5 block text-xs leading-snug text-zinc-500">
                      See a short example, translation and explanation when learning a new pattern.
                    </span>
                  </span>
                  <span
                    aria-hidden="true"
                    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors ${examplesBeforePractice ? "border-orange-300/70 bg-orange-500" : "border-white/15 bg-zinc-800"}`}
                  >
                    <span
                      className={`block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${examplesBeforePractice ? "translate-x-6" : "translate-x-1"}`}
                    />
                  </span>
                </button>
              ) : null}
              <div>
                <label className="block text-sm font-bold text-gray-200 mb-2">Teaching style</label>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button className="flex w-full items-center justify-between rounded-lg border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:border-orange-300/35 hover:bg-white/[0.07]">
                      {selectedTeachingStyle.charAt(0).toUpperCase() +
                        selectedTeachingStyle.slice(1)}
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content
                    sideOffset={6}
                    align="center"
                    className="z-[10001] mt-2 max-h-80 min-w-[280px] overflow-y-auto rounded-lg border border-white/10 bg-[#121318] py-1 shadow-[0_18px_50px_rgba(0,0,0,0.45)]"
                  >
                    {[
                      {
                        value: "neutral",
                        label: "Neutral",
                        description: "Patient mentor who guides and supports",
                      },
                      {
                        value: "cheeky",
                        label: "Cheeky",
                        description: "Witty tutor with humor and playful energy",
                      },
                      {
                        value: "chic",
                        label: "Chic",
                        description: "Sophisticated polyglot with elegant style",
                      },
                      {
                        value: "spartan",
                        label: "Spartan",
                        description: "Strict drill sergeant with zero tolerance",
                      },
                    ].map((style) => (
                      <DropdownMenu.Item
                        key={style.value}
                        onSelect={() => setSelectedTeachingStyle(style.value as TeachingStyle)}
                        className={`px-3 py-2 text-sm cursor-pointer ${
                          selectedTeachingStyle === style.value
                            ? "bg-orange-500/10 text-orange-300"
                            : "text-white hover:bg-orange-500/20"
                        }`}
                      >
                        <div className="flex flex-col">
                          <span className="font-semibold">{style.label}</span>
                          <span className="text-[11px] text-gray-400 leading-tight mt-0.5">
                            {style.description}
                          </span>
                        </div>
                      </DropdownMenu.Item>
                    ))}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label className="block text-sm font-bold text-gray-200" htmlFor="nickname">
                    Nickname
                  </label>
                  <span className="text-[11px] text-gray-500">
                    {nickname.length}/{PERSONALIZATION_NICKNAME_MAX_CHARS}
                  </span>
                </div>
                <input
                  id="nickname"
                  type="text"
                  value={nickname}
                  maxLength={PERSONALIZATION_NICKNAME_MAX_CHARS}
                  onChange={(event) => setNickname(capNicknameInput(event.target.value))}
                  placeholder="What should Chicky call you?"
                  className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-semibold text-white placeholder-gray-500 outline-none transition-colors focus:border-orange-300/55"
                />
              </div>
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <label
                    className="block text-sm font-bold text-gray-200"
                    htmlFor="customInstructions"
                  >
                    Custom Instructions
                  </label>
                  <span className="text-[11px] text-gray-500">
                    {customInstructions.length}/{PERSONALIZATION_CUSTOM_INSTRUCTIONS_MAX_CHARS}
                  </span>
                </div>
                <textarea
                  id="customInstructions"
                  value={customInstructions}
                  maxLength={PERSONALIZATION_CUSTOM_INSTRUCTIONS_MAX_CHARS}
                  onChange={(event) =>
                    setCustomInstructions(capCustomInstructionsInput(event.target.value))
                  }
                  placeholder="Additional behaviour and style preferences"
                  rows={4}
                  className="min-h-24 w-full resize-y rounded-lg border border-white/10 bg-white/[0.04] px-4 py-3 text-sm font-medium leading-relaxed text-white placeholder-gray-500 outline-none transition-colors focus:border-orange-300/55"
                />
              </div>
            </div>
            <div className="space-y-4 rounded-lg border border-white/10 bg-white/[0.025] p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-bold text-gray-200">Session Vocabulary</h3>
                  <p className="mt-1 text-xs text-gray-400">Applied to new speaking sessions.</p>
                </div>
                <span
                  className={`shrink-0 rounded-md border px-2.5 py-1 text-[11px] font-bold ${
                    hasNoVocabularyLimit
                      ? "border-gray-600/60 bg-gray-700/30 text-gray-300"
                      : "border-orange-400/40 bg-orange-500/15 text-orange-200"
                  }`}
                >
                  {currentVocabularyLabel}
                </span>
              </div>

              <label className="block text-sm font-bold text-gray-200">
                <span className="mb-2 block">Vocabulary language</span>
                <span className="block w-full rounded-lg border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm font-semibold text-white">
                  {vocabularyLanguage || "Choose a practice language first"}
                </span>
                <span className="mt-1 block text-xs font-medium text-gray-500">
                  Uses the language selected on the main practice screen.
                </span>
              </label>

              <button
                type="button"
                onClick={handleClearSessionVocabulary}
                aria-pressed={hasNoVocabularyLimit}
                className={`flex w-full items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-all ${
                  hasNoVocabularyLimit
                    ? "border-orange-400/60 bg-orange-500/15 shadow-sm shadow-orange-500/20"
                    : "border-gray-700/50 bg-[#262b3d] hover:border-gray-600"
                }`}
              >
                <span>
                  <span className="block text-sm font-bold text-white">No limit</span>
                  <span className="mt-0.5 block text-xs text-gray-400">
                    Default · the tutor uses its full vocabulary
                  </span>
                </span>
                {hasNoVocabularyLimit ? (
                  <span className="flex shrink-0 items-center gap-1 rounded-md bg-orange-500/20 px-2 py-0.5 text-[11px] font-bold text-orange-200">
                    Active
                  </span>
                ) : (
                  <span className="shrink-0 text-[11px] font-semibold text-gray-500">
                    Tap to reset
                  </span>
                )}
              </button>

              <div>
                <p className="mb-2 text-xs font-bold uppercase text-gray-500">
                  Limit to most common words
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {COMMON_WORD_LIMITS.map((limit) => {
                    const isActive = activeCommonWordLimit === limit;
                    return (
                      <button
                        key={limit}
                        type="button"
                        onClick={() => handleUseCommonWords(limit)}
                        aria-pressed={isActive}
                        className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-all ${
                          isActive
                            ? "border-orange-400/70 bg-orange-500/25 text-white shadow-sm shadow-orange-500/20"
                            : "border-orange-500/20 bg-orange-500/10 text-orange-200 hover:bg-orange-500/20"
                        }`}
                      >
                        {limit} most common
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="mb-2 text-xs font-bold uppercase text-gray-500">Use your own cards</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <button
                    type="button"
                    onClick={() => handleUseFlashcardsForSession("all")}
                    disabled={!vocabularyLanguageFlashcards.length}
                    aria-pressed={isSavedCardsActive}
                    className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                      isSavedCardsActive
                        ? "border-orange-400/70 bg-orange-500/25 text-white"
                        : "border-white/10 bg-white/[0.04] text-white/70 hover:border-orange-300/35 hover:bg-white/[0.07]"
                    }`}
                  >
                    Saved cards ({vocabularyLanguageFlashcards.length})
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUseFlashcardsForSession("favorites")}
                    disabled={!vocabularyLanguageFavorites.length}
                    aria-pressed={isFavoritesActive}
                    className={`flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                      isFavoritesActive
                        ? "border-orange-400/70 bg-orange-500/25 text-white"
                        : "border-white/10 bg-white/[0.04] text-white/70 hover:border-orange-300/35 hover:bg-white/[0.07]"
                    }`}
                  >
                    Favorites ({vocabularyLanguageFavorites.length})
                  </button>
                </div>
              </div>

              {vocabularyStatus && (
                <p className="text-sm font-medium text-orange-200">{vocabularyStatus}</p>
              )}
            </div>
            {allowBackgroundSelection && (
              <BackgroundVideoSelector
                defaultSelectedId={backgroundVideoDefaultId}
                fallbackVideoUrl={backgroundVideoFallbackUrl}
              />
            )}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mt-6 flex justify-end"
            >
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="h-11 rounded-lg border border-orange-300/70 bg-orange-500 px-6 text-sm font-bold text-white transition-colors hover:bg-orange-400"
              >
                Done
              </motion.button>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>

      <ProFeatureUpgradeModal
        isOpen={upgradeFeature !== null}
        feature={upgradeFeature || "resume_session"}
        onClose={() => setUpgradeFeature(null)}
        source={upgradeFeature ? `settings_${upgradeFeature}` : "settings_locked_feature"}
        userId={user?.id}
        userEmail={user?.email}
      />
    </AnimatePresence>
  );
};

export default SettingsModal;
